import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Rtl2SsoService } from '@rtl2/sso';
import { ApiAbstractService, SharedEnvironmentService } from '@shared/services/system';
import { MazCardBean, BobyResponse } from '@app/models';
import { environment } from 'src/environments/environment';

export const ApiPropoEndpoints = {
  ValidationInfo: '/b4wapi-core/mazKarte/v2/validationInfo',
  SearchTitle: '/b4wapi-core/propo/v2/search',
  CheckRtl2Employee: '/b4wapi-core/admin/myself/isRtl2',
  SearchEpisode: '/b4wapi-core/propo/v2/search',
  SearchMaz: '/b4wapi-core/propo/v2/search',
  GetNewFile: '/b4wapi-core/mazKarte/v2/template/{mazNr}',
  GetFile: '/b4wapi-core/mazKarte/v2/{mazNr}/{bandNr}',
  DeleteFile: '/b4wapi-core/mazKarte/v2/{mazNr}/file/{bandNr}',
  GetFilePdf: '/b4wapi-core/mazKarte/v2/pdf/{mazNr}/{bandNr}',
  GetFileCount: '/b4wapi-core/mazKarte/v2/{mazNr}/count',
  GetMazInfo: '/b4wapi-core/mazKarte/v2/{mazNr}',
  GetMazDraftInfo: '/b4wapi-core/mazKarte/v2/{mazNr}/draft',
  SaveMazFile: '/b4wapi-core/mazKarte/v2/{mazNr}/{bandNr}',
  SaveMazFileDraft: '/b4wapi-core/mazKarte/v2/{mazNr}/draft',
  DeleteMazFileDraft: '/b4wapi-core/mazKarte/v2/{mazNr}/draft',
  PublishMazFile: '/b4wapi-core/mazKarte/v2/{mazNr}/{bandNr}/publish',
};

export const ApiInfo = {
  GetDatenshutz: '/documents/getByIdentifier?identifier=dataproducer',
  GetImpressum: '/documents/getByIdentifier?identifier=impressumproducer',
};

@Injectable({
  providedIn: 'root',
})
export class ApiService extends ApiAbstractService {
  constructor(
    protected http: HttpClient,
    protected sso: Rtl2SsoService,
    private env: SharedEnvironmentService
  ) {
    super(http, sso);
    this.apiServiceUrl = environment.urlBobyApi;
  }

  public getTokenHttpHeaders() {
    return new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('x-b4-token', this.sso.getToken())
      .set('x-b4-client', this.env.get('clientToken'));
  }

  public searchTitle(query: string) {
    const params = new HttpParams().append('formatTitle', query);
    return this.get(ApiPropoEndpoints.SearchTitle, {
      params: params,
      headers: this.getTokenHttpHeaders(),
    });
  }

  public searchEpisode(title: string, query: string) {
    const params = new HttpParams().append('formatTitle', title).append('episodeNr', query);

    return this.get(ApiPropoEndpoints.SearchEpisode, {
      params: params,
      headers: this.getTokenHttpHeaders(),
    });
  }

  public searchMaz(query: string) {
    const params = new HttpParams().append('mazNr', query);
    return this.get(ApiPropoEndpoints.SearchMaz, {
      params: params,
      headers: this.getTokenHttpHeaders(),
    });
  }

  public episodePagination(format: string, start: string) {
    const params = new HttpParams().append('formatId', format).append('index', start);
    return this.get(ApiPropoEndpoints.SearchTitle, {
      params: params,
      headers: this.getTokenHttpHeaders(),
    });
  }

  public getNewFile(mazNr: string) {
    const params = new HttpParams();
    const url = ApiPropoEndpoints.GetNewFile.replace(/\{mazNr\}/gi, mazNr);
    return this.get<BobyResponse<any>>(url, {
      params: params,
      headers: this.getTokenHttpHeaders(),
    });
  }

  public getFileCount(mazNr: string) {
    let url = ApiPropoEndpoints.GetFileCount;
    url = url.replace(/\{mazNr\}/gi, mazNr);

    return this.get<BobyResponse<MazCardBean>>(url, this.getTokenHeader());
  }

  public getFile(mazNr: string, fileNr: string) {
    let url = ApiPropoEndpoints.GetFile;
    url = url.replace(/\{mazNr\}/gi, mazNr).replace(/\{bandNr\}/gi, fileNr);

    return this.get<BobyResponse<MazCardBean>>(url, this.getTokenHeader());
  }

  public getFilePdf(mazNr: string, fileNr: string) {
    const headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('x-b4-token', this.sso.getToken())
      .set('x-b4-client', this.env.get('clientToken'));
    const httpOptions = {
      headers: headers,
      responseType: 'blob',
    };
    let url = ApiPropoEndpoints.GetFilePdf;
    url = url.replace(/\{mazNr\}/gi, mazNr).replace(/\{bandNr\}/gi, fileNr);

    return this.get(url, httpOptions);
  }

  public deleteFile(mazNr: string, fileNr: string) {
    let url = ApiPropoEndpoints.DeleteFile;
    url = url.replace(/\{mazNr\}/gi, mazNr).replace(/\{bandNr\}/gi, fileNr);

    return this.delete<Response>(url, this.getTokenHeader());
  }

  public getMazInfo(mazNr: string) {
    let url = ApiPropoEndpoints.GetMazInfo;
    url = url.replace(/\{mazNr\}/gi, mazNr);

    return this.get<BobyResponse<any>>(url, this.getTokenHeader());
  }

  public saveFile(mazNr, fileNr, file: MazCardBean) {
    let url = ApiPropoEndpoints.SaveMazFile;
    url = url.replace(/\{mazNr\}/gi, mazNr).replace(/\{bandNr\}/gi, fileNr);
    return this.put<BobyResponse<MazCardBean>>(url, file, this.getTokenHeader());
  }

  public publishMazFIle(mazNr, fileNr, file: MazCardBean) {
    let url = ApiPropoEndpoints.PublishMazFile;
    url = url.replace(/\{mazNr\}/gi, mazNr).replace(/\{bandNr\}/gi, fileNr);
    return this.post<BobyResponse<MazCardBean>>(url, file, this.getTokenHeader());
  }

  public validationInfo() {
    const url = ApiPropoEndpoints.ValidationInfo;
    return this.get(url, this.getTokenHeader());
  }

  public getDatenshutz() {
    const environment = this.env.get('urlSsoB2bWebServer');
    const url = environment + ApiInfo.GetDatenshutz;
    return this.http.get(url);
  }

  public getImpressum() {
    const environment = this.env.get('urlSsoB2bWebServer');
    const url = environment + ApiInfo.GetImpressum;
    return this.http.get(url);
  }

  public getRtl2Employee() {
    const url = ApiPropoEndpoints.CheckRtl2Employee;
    return this.get<BobyResponse<MazCardBean>>(url, this.getTokenHeader());
  }

  public getMazDraft(mazNr: string) {
    let url = ApiPropoEndpoints.GetMazDraftInfo;
    url = url.replace(/\{mazNr\}/gi, mazNr);

    return this.get<BobyResponse<any>>(url, this.getTokenHeader());
  }

  public saveMazDraft(mazNr, file: MazCardBean) {
    let url = ApiPropoEndpoints.SaveMazFileDraft;
    url = url.replace(/\{mazNr\}/gi, mazNr);
    return this.put<BobyResponse<MazCardBean>>(url, file, this.getTokenHeader());
  }

  public deleteMazDraft(mazNr) {
    let url = ApiPropoEndpoints.DeleteMazFileDraft;
    url = url.replace(/\{mazNr\}/gi, mazNr);
    return this.delete<Response>(url, this.getTokenHeader());
  }
}
